import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import LeafIcon from '../../image/icons/LeafIcon.svg';
import BlackClockIcon from '../../image/icons/BlackClockIcon.svg';
import { TripService } from '../../services/TripService';
import { type UserChoice, type Route } from '../../types/Forms';
import { type DepartureAndArrival, type Location } from '../../types/Autocomplete';
import { type RouteHistory } from '../../types/GlobalContext';
import { useLocationContext } from '../../store/LocationContext';
import { formatDuration } from '../../tools/TimeRelated';
import { useEphemeral } from '../../store/EphemeralContext';
// import { DebugUtils } from '../../tools/DebugUtils';

const tripService = new TripService();

interface CarTripButtonProps {
  type: 'autoroute_peages' | 'no_autoroute_peages' | 'eco_friendly';
  selectedInputs: DepartureAndArrival;
  userChoice: UserChoice;
  updateUserChoice: (updates: Partial<UserChoice>) => void;
  isReturn?: boolean;
  setSelectedItinerary: Dispatch<SetStateAction<string>>;
  selectedItinerary: string;
  itineraries: Route[];
  setItineraries: Dispatch<SetStateAction<Route[]>>;
  setGoItineraries: Dispatch<SetStateAction<Route[]>>;
  setBackupReturnItineraries: Dispatch<SetStateAction<Route[]>>;
}

const CarTripButton: React.FC<CarTripButtonProps> = ({
  type,
  selectedInputs,
  userChoice,
  updateUserChoice,
  isReturn = false,
  selectedItinerary,
  setSelectedItinerary,
  itineraries,
  setItineraries,
  setGoItineraries,
  setBackupReturnItineraries
}) => {
  const [route, setRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { state: { routeHistory, trackingMode }, addRouteHistory } = useEphemeral();

  const { userLocation } = useLocationContext();
  const [searchLocation, setSearchLocation] = useState<{ latitude: number, longitude: number }>(userLocation);
  const inputsRef = useRef(selectedInputs);

  useEffect(() => {
    if (inputsRef.current !== selectedInputs) {
      setSearchLocation(userLocation);
    }
  }, [selectedInputs, userLocation]);

  /**
   * Met à jour la position courante de l'utilisateur
   */
  const userLocationFill = useCallback((loc: Location) => {
    const newLoc = { ...loc };
    if (loc.lat === -1 && loc.lat === -1 && !trackingMode) {
      newLoc.lat = searchLocation.latitude;
      newLoc.lon = searchLocation.longitude;
    }
    return newLoc;
  }, [searchLocation.latitude, searchLocation.longitude, trackingMode]);

  useEffect(() => {
    if (trackingMode) return;
    const getRoutes = async () => {
      const parameters = {
        source: isReturn ? userLocationFill(selectedInputs.arrival) : userLocationFill(selectedInputs.departure),
        destination: isReturn ? userLocationFill(selectedInputs.departure) : userLocationFill(selectedInputs.arrival),
        type,
        startDateTime: Date.now()
      };

      const existingRoute = routeHistory.find(
        (entry: { parameters: { source: { lat: number; lon: number }; destination: { lat: number; lon: number }; type: string } }) =>
          entry.parameters.source.lat === parameters.source.lat &&
          entry.parameters.source.lon === parameters.source.lon &&
          entry.parameters.destination.lat === parameters.destination.lat &&
          entry.parameters.destination.lon === parameters.destination.lon &&
          entry.parameters.type === parameters.type
      );

      if (existingRoute !== null && existingRoute !== undefined) {
        setRoute(existingRoute.response);
        if (isReturn) {
          setBackupReturnItineraries((lastItineraries) => {
            const itineraryExists = lastItineraries.some((itinerary) => itinerary.line === existingRoute.response.line);
            if (!itineraryExists) {
              return [...lastItineraries, { ...existingRoute.response, isReturn: !!isReturn }];
            } else return lastItineraries;
          });
        } else {
          setGoItineraries((lastItineraries) => {
            const itineraryExists = lastItineraries.some((itinerary) => itinerary.line === existingRoute.response.line);
            if (!itineraryExists) {
              return [...lastItineraries, { ...existingRoute.response, isReturn: !!isReturn }];
            } else return lastItineraries;
          });
        }
        setLoading(false);
      } else {
        const response = await tripService.getItinerary(parameters);
        if (response.data !== null) {
          setRoute(response.data);

          if (isReturn) {
            setBackupReturnItineraries((lastItineraries) => {
              const itineraryExists = lastItineraries.some((itinerary) => itinerary.line === response.data.line);
              if (!itineraryExists) {
                return [...lastItineraries, { ...response.data, isReturn: !!isReturn }];
              } else return lastItineraries;
            });
          } else {
            setGoItineraries((lastItineraries) => {
              const itineraryExists = lastItineraries.some((itinerary) => itinerary.line === response.data.line);
              if (!itineraryExists) {
                return [...lastItineraries, { ...response.data, isReturn: !!isReturn }];
              } else return lastItineraries;
            });
          }

          const routeHistory: RouteHistory = { parameters, response: response.data, timestamp: Date.now() };

          addRouteHistory(routeHistory);
        }
        setLoading(false);
      }
    };

    void getRoutes();
  }, [isReturn, selectedInputs.arrival, selectedInputs.departure, setBackupReturnItineraries, setGoItineraries, setItineraries, type, routeHistory, addRouteHistory, userLocationFill, trackingMode]);

  if (loading) {
    return <></>;
  }

  return route !== null ? (
    <div className="mr-1 relative min-w-[100px]">
      {route.type === 'eco' && <img src={LeafIcon} className="absolute -top-2 -right-2" />}
      <div
        className={`text-sm w-full py-1 px-2 cursor-pointer border-2 rounded-xl bg-white ${isReturn
          ? userChoice.returnRoute?.id === route.id
            ? 'border-leaf-green'
            : 'border-ionic-border-light-gray'
          : userChoice.route?.id === route.id
            ? 'border-leaf-green'
            : 'border-ionic-border-light-gray'
          }`}
        onClick={() => {
          setSelectedItinerary(route.id);
          updateUserChoice({ [isReturn ? 'returnRoute' : 'route']: route });
        }}
      >
        <div className="flex items-center justify-start">
          <img src={BlackClockIcon} className="mr-1" />
          <p className="text-black">{`${formatDuration(route.duration * 60)} min`}</p>
        </div>
        <p className={`${route.type === 'eco' ? 'text-leaf-green' : route.type === 'sp' ? 'text-ionic-secondary' : 'text-ionic-orange'}`}>
          {route.type === 'p' && 'Péages'}
          {route.type === 'sp' && 'Sans Péages'}
          {route.type === 'eco' && 'ECO'}
        </p>
        <div className="flex items-center text-ionic-gray">
          <p>{`${route.distance} Km`}</p>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default CarTripButton;
